/**
 * Vidensbankens sideregister ([D-450], 27-10-2026) — ÉN liste over
 * dokumentationssiderne under Dokumentation, med de filer hver side består af
 * og de kildefiler den beskriver.
 *
 * Bruges af 2 vagter:
 *  - CI-gaten `check:doc-pages` (server/src/scripts/check-doc-pages.ts, uden
 *    DB): hver side i web/src/views/docs findes her og har rute, katalogpost og
 *    menupunkt, alle filer findes, og links på siderne og i ordbogen peger på
 *    ruter der findes.
 *  - Dokumentationsvagten (server/src/lib/docs-vagt.ts, hver nat i prod):
 *    ændres en side's `sources`, eller kommer der nye beslutninger i D-loggen
 *    der nævner sidens emne, efterprøver vagten siden mod koden og
 *    beslutningerne (12-conventions §91: retter selv, hvor det er sikkert).
 *
 * `pageFiles` er sidens egne filer (view + data-modul der kun serverer siden);
 * ændres de, er siden redigeret. `sources` er koden siden BESKRIVER; de skal
 * ligge i server/src eller shared/src, fordi kun de findes i prod-imaget.
 * `topics` er ord der i en beslutning peger på siden (ud over stien og
 * "Dokumentation → <titel>"); de skal være præcise, for hvert match koster en
 * efterprøvning. `allDecisions` = enhver ny beslutning kan berøre siden
 * (Ordbogen).
 *
 * Ny side: tilføj den her i samme leverance som ruten, katalogposten og
 * menupunktet (CI-gaten fejler ellers).
 */

export interface DocPage {
  /** Stabil nøgle (bruges i vagtens tilstand og fund). */
  key: string;
  /** Rute uden '#', fx '/doc/budget'. */
  path: string;
  /** Menupunktets tekst under Dokumentation. */
  title: string;
  /** Sidens egne filer, relativt til repo-roden. Den første er view'et. */
  pageFiles: string[];
  /** Koden siden beskriver (server/src eller shared/src). */
  sources: string[];
  /** Præcise ord der i en beslutning peger på siden. */
  topics: string[];
  /** Enhver ny beslutning kan berøre siden (Ordbogen). */
  allDecisions?: boolean;
}

export const DOC_PAGES: readonly DocPage[] = [
  {
    key: 'phone',
    path: '/doc/phone',
    title: 'Telefon',
    pageFiles: ['web/src/views/docs/TelefonNaevnerDocView.vue', 'server/src/lib/tdc-queue-gaps.ts'],
    sources: [
      'server/src/lib/tdc-exclusions.ts',
      'server/src/lib/tdc-mail-agent.ts',
      'server/src/lib/tdc-report-import.ts',
      'shared/src/tdc-queues.ts',
    ],
    topics: ['telefon-nævner', 'TDC', 'kø-rapport', 'CDR'],
  },
  {
    key: 'store-visits',
    path: '/doc/store-visits',
    title: 'Butiksbesøg',
    pageFiles: ['web/src/views/docs/ButiksbesoegDocView.vue', 'server/src/lib/butiksbesoeg-doc.ts'],
    sources: [
      'server/src/lib/store-visits-source.ts',
      'server/src/lib/store-visit-days.ts',
      'shared/src/store-visit-days.ts',
      'server/src/routes/butiksbesog.ts',
    ],
    topics: ['butiksbesøg', 'kameraet', 'Meraki', 'tællelinje'],
  },
  {
    key: 'revenue',
    path: '/doc/attribution/revenue',
    title: 'Omsætning',
    pageFiles: [
      'web/src/views/docs/RevenueDocView.vue',
      'web/src/views/docs/revenueFlows.ts',
      'server/src/lib/revenue-model-doc.ts',
    ],
    sources: [
      'server/src/lib/rf-waterfall.ts',
      'server/src/lib/marketing-rates.ts',
      'server/src/lib/accept-dating.ts',
      'shared/src/revenue-texts.ts',
    ],
    topics: ['R/A/~F', 'accept-dat', 'returrate', 'rf-waterfall', 'marketing-rates', 'natjob'],
  },
  {
    key: 'gross-profit',
    path: '/doc/gross-profit',
    title: 'Bruttofortj. og avancer',
    pageFiles: ['web/src/views/docs/BruttofortjenesteDocView.vue'],
    sources: [
      'server/src/lib/rabatstyring-metrics.ts',
      'server/src/lib/growth-cases/rabat-bruttofortjeneste-live.ts',
      'server/src/lib/salgsstatistik.ts',
      'server/src/lib/accounting-index.ts',
    ],
    topics: ['bruttofortjeneste', 'bruttoavance', 'dækningsgrad', 'værdiposter', 'kostbeløb', 'konto 2150'],
  },
  {
    key: 'budget',
    path: '/doc/budget',
    title: 'Budget',
    pageFiles: ['web/src/views/docs/BudgetDocView.vue'],
    sources: [
      'server/src/lib/budget-pl-mirror.ts',
      'server/src/lib/budget-recon-matcher.ts',
      'server/src/lib/price-adj-budget-rows.ts',
    ],
    topics: ['Regnskab → Budget', 'budgetrække', 'Periodeafstemning', 'restrække', 'budget_rows'],
  },
  {
    key: 'glossary',
    path: '/doc/glossary',
    title: 'Ordbog',
    pageFiles: ['web/src/views/docs/GlossaryDocView.vue', 'web/src/lib/glossary-terms.ts'],
    sources: [],
    topics: ['ordbog'],
    allDecisions: true,
  },
  {
    key: 'freshness',
    path: '/doc/freshness',
    title: 'Datafriskhed',
    pageFiles: ['web/src/views/docs/FreshnessDocView.vue', 'server/src/lib/data-freshness-doc.ts'],
    sources: [
      'server/src/lib/ops-status.ts',
      'server/src/scripts/sync-bc-cascade.ts',
      'server/src/scripts/sync-marketing-cascade.ts',
    ],
    topics: ['datafriskhed', 'natjob', 'kaskade', 'job-heartbeat'],
  },
  {
    key: 'security',
    path: '/doc/security',
    title: 'Sikkerhedspolitik',
    pageFiles: ['web/src/views/docs/SecurityDocView.vue'],
    sources: [
      'server/src/lib/user-permissions.ts',
      'server/src/lib/api-authz.ts',
      'shared/src/klaviyo-guardrails.ts',
      'server/src/lib/security-hardening.ts',
    ],
    topics: ['sikkerhed', 'grant', 'rolle', 'BC Production', 'guardrail', 'skrivelag', 'hvidliste'],
  },
  {
    key: 'agents',
    path: '/doc/agents',
    title: 'Agenter',
    pageFiles: ['web/src/views/docs/AgentsDocView.vue', 'server/src/lib/agents-doc.ts'],
    sources: ['server/src/lib/ops-status.ts', 'server/src/lib/llm-tasks.ts'],
    topics: ['agent', 'vagten', 'natjob', 'retter'],
  },
  {
    key: 'seo',
    path: '/doc/seo',
    title: 'SEO',
    pageFiles: ['web/src/views/docs/SeoDocView.vue'],
    sources: [
      'server/src/lib/seo-agent-scheduler.ts',
      'server/src/lib/seo/rules-registry.ts',
      'server/src/lib/seo/publisher.ts',
      'shared/src/brand-voice.ts',
    ],
    topics: ['SEO', 'regelsæt', 'meta-titel', 'metabeskrivelse', 'brand-voice'],
  },
  {
    key: 'customer-match',
    path: '/doc/customer-match',
    title: 'Customer Match',
    pageFiles: ['web/src/views/docs/CustomerMatchDocView.vue', 'server/src/lib/customer-match-doc.ts'],
    sources: [
      'server/src/lib/customer-match/build.ts',
      'server/src/lib/customer-match/config.ts',
      'server/src/lib/customer-match/conversions.ts',
      'server/src/lib/customer-match/run.ts',
    ],
    topics: ['Customer Match', 'offline-konvertering', 'Data Manager', 'Conversions API', 'Indsigelse'],
  },
];

/** Opslag på nøgle; undefined hvis siden ikke findes. */
export function docPageByKey(key: string): DocPage | undefined {
  return DOC_PAGES.find((p) => p.key === key);
}

/**
 * Rettelseslaget ([D-478]): en ordret passage som regex, hvor hvert mellemrum
 * matcher ethvert whitespace. Skabeloner brydes over linjer i kildefilen, men
 * står med enkelte mellemrum på siden; serveren (værnene) og webappen
 * (DocPatchLayer) matcher derfor med samme regex.
 */
export function docPatchRegex(find: string, flags = 'g'): RegExp {
  const parts = find
    .trim()
    .split(/[\s\u00a0]+/)
    .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(parts.join('[\\s\\u00a0]+'), flags);
}
